<template>
  <!-- OVERLAY -->
  <div
    v-if="open"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-1"
    @click.self="close"
  >
    <!-- MODAL -->
    <div
      class="w-full max-w-lg rounded-2xl bg-[#0f172a] border border-white/10 p-6 shadow-xl"
    >
      <!-- HEADER -->
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-lg font-semibold">Suggest a link</h2>
        <button
          @click="close"
          class="text-white/60 hover:text-white text-xl"
        >
          ✕
        </button>
      </div>

      <div class="grid grid-cols-1 flex mb-6">
        <p v-if="data.step == 0" class="mt-1">Participate in the Platform.</p>
        <p v-else class="mt-1">Thanks for contributing! We’ll review your link shortly.</p>
        <p v-if="data.step == 0" class="mt-4">Assist us in maintaining the quality of our material.</p>
        <p v-else class="mt-4">Want feedback on your suggestion? Leave your email (optional).</p>
      </div>
      <!-- FORM -->
      <form @submit.prevent="submit">
        <div class="grid grid-cols-1" v-if="data.step == 0">
          <div class="relative">
            <input
              v-model="data.item.url"
              type="url"
              required
              placeholder="https://example.com"
              maxlength="255"
              @change="changeEvent"
              class="input rounded-xl bg-white/10 backdrop-blur px-2 py-3 w-full"
            />
            <svg v-if="data.isScrapping" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width1="1.5" stroke="currentColor" 
              class="pointer-events-none w-8 h-8 absolute top-1/2 transform -translate-y-1/2 right-3 size-6" id="hourglass">
              <path fill="transparent" stroke-linecap="round" stroke-linejoin="round" d="M19.5 12c0-1.232-.046-2.453-.138-3.662a4.006 4.006 0 0 0-3.7-3.7 48.678 48.678 0 0 0-7.324 0 4.006 4.006 0 0 0-3.7 3.7c-.017.22-.032.441-.046.662M19.5 12l3-3m-3 3-3-3m-12 3c0 1.232.046 2.453.138 3.662a4.006 4.006 0 0 0 3.7 3.7 48.656 48.656 0 0 0 7.324 0 4.006 4.006 0 0 0 3.7-3.7c.017-.22.032-.441.046-.662M4.5 12l3 3m-3-3-3 3" />
            </svg>
          </div>

          
          <input
            id="text"
            ref="inputText"
            v-model="data.item.title"
            type="text"
            required
            placeholder="Title"
            maxlength="255"
            class="input rounded-xl bg-white/10 backdrop-blur px-2 py-3 mt-4"
          />
          
          <textarea
              v-model="data.item.description"
              rows="3"
              maxlength="4096"
              placeholder="Description"
              class="input resize-none rounded-xl bg-white/10 backdrop-blur px-2 py-3 mt-4"></textarea>

          <Tags :tags="data.item.tags"></Tags>

          <div class="mt-4">
            <file-upload2 :files="data.item.images" maxUploadedFiles="6" ref="images"></file-upload2>
          </div>          
        </div>
        <div class="grid grid-cols-1 gap-4" v-if="data.step == 1">
          <input
            v-model="data.item.email"
            type="text"
            required
            placeholder="Email"
            maxlength="255"
            class="input rounded-xl bg-white/10 backdrop-blur px-2 py-3"
          />
          <input
            v-model="data.item.name"
            type="text"
            required
            placeholder="Your Name"
            maxlength="100"
            class="input rounded-xl bg-white/10 backdrop-blur px-2 py-3"
          />
        </div>

        <!-- ACTIONS -->
        <div class="flex justify-end gap-3 mt-6">
          <div class="py-2 text-red-400" v-if="data.error">{{ data.error }}</div>
          <button
            type="button"
            @click="close"
            class="px-4 py-2 rounded-lg text-white/70 hover:text-white">
            Cancel
          </button>

          <button
            v-if="data.step == 0"
            type="button"
            class="px-5 py-2 rounded-lg bg-cyan-400 text-black font-semibold hover:bg-cyan-300"
            @click.prevent="saveItem()">
            Add Link
          </button>
          <button
            v-if="data.step == 1"
            type="button"
            class="px-5 py-2 rounded-lg bg-cyan-400 text-black font-semibold hover:bg-cyan-300"
            @click.prevent="updateName()">
            Submit
          </button>
        </div>
      </form>
    </div>
  </div>
</template>
<script lang="ts">
  import { watchEffect, reactive, watch, ref } from "vue"

  import scrapperService from "@/services/scrapper.service";
  import linkService from "@/services/link.service";
  import FileUpload2 from "@/components/FileUpload2.vue"; 
  import Tags from './Tags.vue'
  import { isValidHttpUrl } from "@/utils/url.utils";

  const inputText = ref();
  const images = ref();
  export default {
    name: 'LinksEditComponent',
    components: {
      FileUpload2,
      Tags
    },
    props: {
        // Define your props here
        open: {
            type: Boolean,
            required: true,
            default: false,
        }
    },
    emits: ["close", "submit"],
    setup(props: any,{emit}: any) {

      const data = reactive({
        item: {} as any,    
        category: "",
        results: [] as string[],
        step: 0,
        dummy: "",
        error: "",
        isScrapping: false as boolean
      })

      const close = () =>  {
        clearData();
        emit("close")
      }

      const submit = () => {
        emit("submit", { ...data })
        close()
      }

      watchEffect(() => {
        if (inputText.value) {
          inputText.value.focus()
        }
      })

      // fechar com ESC
      watch(
        () => props.open,
        (value) => {
          if (value) {
            clearData();
            window.addEventListener("keydown", onEsc)
          } else {
            window.removeEventListener("keydown", onEsc)
          }
        }
      )

      const onEsc = (e: any) => {
        if (e.key === "Escape") close()
      }

      const clearData = () => {
        data.error = "";
        data.item = {}
        // data.url = "";
        // data.title = "";
        // data.description = "";
        data.results = [];
        data.step = 0;
        // data.error = ""
        // data.item.images = []
        data.item.tags = [];
      }

      const saveAndSetImages = async (path: string) => {
          try
          {
            if (images?.value)
              return await images.value.uploadFiles(path);
            return null;
          } catch (error: any) {
            console.debug("Error uploading files" + error.messsage)
          }
      }

      const updateItem = (item: any) => {
          linkService.updateItem(item._id, item).then(async (response: any) => {
            if (response && response.data && response.data.data) {
              data.item = response.data.data;
            }                    
          })
      }

      const getImages = (files) => {
          var items = [] as string[]
          for (var index = 0; index < files.length; index++) {
            items.push(files[index].preview)
          }
          return items;
        }
        const saveItem = async () => { 
          
            const item = JSON.parse(JSON.stringify(data.item));
            item.images = getImages(images.value.getFiles());
            // insert link
            linkService.insertItem(item, "link").then(async (response: any) => {
              if (response && response.data && response.data.data) {
                const newItem = response.data.data;
                setTimeout(async () => {

                  // upload images
                  var files = await saveAndSetImages('/links/addFile/'+(newItem._id || '')+'/thumbs');
                  if (files != null && files.length > 0) {
                    for (var index = 0; index < files.length; index++) {
                      if ((!newItem.images[index].startsWith("./public")) && files[index] !== newItem.images[index]) {
                        newItem.images[index] = files[index];
                      }
                    }
                    updateItem(newItem);
                    data.step = data.step + 1;
                  } else {
                    data.item = response.data.data; 
                  }
                }, 150)
              } else {
                console.debug("Error inserting link");
                data.error = "Problem inserting link.  Please try again.";
              }
            }).catch((error) => {
              console.debug("Error inserting link", error);
              data.error = "Problem inserting link. Please try again.";
            }) 
        }      

      const updateName = () => {
        linkService.updateItem(data.item._id, {
          name: data.item.name,
          email: data.item.email
        }).then( (response: any) => {
          if (response && response.data) {
            console.debug("Link inserted:", response.data);
            data.step = data.step + 1;
          } else {
            console.debug("Error inserting link");
          }

          console.debug("insertLink response", response)
        } );    
        close();  
      }

      const getInfoFromUrl = async (url: string) => {
        try {
          if (isValidHttpUrl(url)) {
            data.isScrapping = true;
            const result = await scrapperService.getData(url)
            if (result.data.error === 0) {
              if ((data.item.title ?? "").trim() === "")
                data.item.title = result.data.data.title;
              if ((data.item.description ?? "") === "")
                data.item.description = result.data.data.description;
              }

              if (data.item.images === undefined || data.item.images === null)
                data.item.images = [];

              if (data.item.images.length === 0 && (result.data.data?.image ?? "") !== "") {
                var image = result.data.data.image;
                if (image && image.startsWith("/")) {
                  const u = new URL(data.item.url).hostname;
                  image = "https://" + u + image;
              }
              
              data.item.images.push(image)
            }
            data.isScrapping = false;
          }
        } catch (error: any) {
          console.log(error.messsage);
          data.isScrapping = false;
        }

      }

        const changeEvent = async () => {
          if ((data.item.url ?? "").length > 3 && (!data.item.url.startsWith("http")))
            data.item.url = "https://" + data.item.url;

          getInfoFromUrl(data.item.url);
        }
              
        return { submit, data, close, updateName, saveItem, images, changeEvent } ;
  }
}
</script>

<style scoped>
.input {
  @apply w-full rounded-xl bg-white/10 backdrop-blur px-4 py-3
         text-white placeholder-white/40
         focus:outline-none focus:ring-2 focus:ring-cyan-400;
  padding-left: 15px;
  padding-right: 15px;
}

@keyframes flip {
  0% {
    transform: rotate(0deg);
  }
  25% {
    transform: rotate(90deg);
  }
  75% {
    transform: rotate(180deg);
  }
  100% {
    transform: rotate(350deg);
  }
}

#hourglass,
#hourglass #upper-fill-clip,
#hourglass #lower-fill-clip {
  fill: #000000;
}

#hourglass {
  animation-name: flip;
  animation-duration: 3s;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
  transform-origin: center center;
}
</style>